import type { ReportMetadata } from '../types';

/**
 * Ist das ein SiFa-Projekt (Fachkraft fuer Arbeitssicherheit)?
 *
 * Ein SiFa-Projekt ist eine UNBEFRISTETE BETREUUNG eines Betriebs, keine
 * Baustelle (docs/sifa-betreuung-plan.md). Es hat keinen Begehungsturnus,
 * kein geplantes Ende und wird nie "fertig" — jede Liste, Ampel, Warnung
 * oder Rechnungspruefung, die eine Baustelle voraussetzt, muss es deshalb
 * auslassen. Die Weiche liegt hier an EINER Stelle, damit sie ueberall
 * gleich entscheidet und nicht als Copy-Paste in zwanzig Dateien driftet.
 *
 * Nimmt wahlweise das Projekt (DB-Zeile mit `data.metadata`, wie in den
 * Listen) oder die Metadaten selbst (wie in Hub und Berichten). Fehlt der
 * Typ, ist es ein SiGeKo-Projekt aus dem Altbestand — also `false`.
 */
export const istSifaProjekt = (
    source: { data?: { metadata?: Partial<ReportMetadata> | null } | null }
        | Partial<ReportMetadata>
        | null
        | undefined,
): boolean => {
    if (!source || typeof source !== 'object') return false;
    const asProject = source as { data?: { metadata?: Partial<ReportMetadata> | null } | null };
    const meta: Partial<ReportMetadata> | null | undefined =
        asProject.data && typeof asProject.data === 'object'
            ? asProject.data.metadata
            : (source as Partial<ReportMetadata>);
    return meta?.projectType === 'sifa';
};

/** Ein Projekt, soweit sein Begehungsstand hier interessiert (DB-Zeile mit `data.metadata`). */
export interface BegehungsstandQuelle {
    current_inspection_number?: number | null;
    data?: { metadata?: Partial<ReportMetadata> | null } | null;
}

/**
 * Laufende Begehungsnummer: die Spalte `current_inspection_number`, sonst die
 * Nummer im Dokument, sonst 0.
 *
 * Die Spalte ist nullbar. Leer heisst „nie gesetzt", nicht „unbekannt" — es
 * zaehlt wie eine fehlende Spalte (Betreiberentscheid 05.10.2026). Frueher
 * reichte die Projektverwaltung `null` durch: ein solches Projekt war weder V0
 * noch V1 und fiel aus dem Filter „ohne Begehung" heraus.
 */
export const begehungsNummer = (project: BegehungsstandQuelle): number =>
    project.current_inspection_number ?? project.data?.metadata?.inspectionNumber ?? 0;

/**
 * Noch keine Begehung durchgefuehrt (Stand „V0")?
 *
 * Nicht mitgezaehlt: berichtsbefreite Projekte und SiFa-Betreuung — beide
 * haben keinen Begehungsturnus, „ohne Begehung" ist dort kein Befund.
 * Projektverwaltung (V0-Filter) und Tourenkarte fragen hier.
 */
export const hatNochKeineBegehung = (project: BegehungsstandQuelle): boolean => {
    if (project.data?.metadata?.globalData?.reportExempt === true) return false;
    if (istSifaProjekt(project)) return false;
    return begehungsNummer(project) === 0;
};
